// Doble factor TOTP del portal (plan-seguridad-alto-nivel-2026-Q4, control 2b;
// .claude/feature-log/FEAT-20261026-mfa-totp.md).
//
// Constantes y funciones puras compartidas por AuthContext (reto en el login), el modal del
// reto, la pantalla de enrolamiento, el menú de perfil, functionsClient (403 MFA_REQUIRED) y la
// ficha de usuario (EditarUsuarioV2: quitar el doble factor de otro usuario).
// Sin React y sin matrices de roles: qué roles EXIGEN doble factor lo decide solo el backend
// (MFA_REQUIRED_ROLES en auth.middleware.js); el cliente se entera por el 403.

import { isSA } from './userHierarchy';

// Pantalla completa (sin PrivateLayoutV2) desde la sesión 4 del feature-log: PrivateRouter la
// detecta con matchPath sobre esta misma constante.
export const MFA_ENROLL_PATH = '/seguridad/doble-factor';

// Quién VE la opción (decisión del 2026-10-26): el doble factor es solo para super-admins.
// La ruta vive únicamente en la bifurcación SA de PrivateRouter; el menú de perfil y la
// redirección de functionsClient usan este mismo predicado para no mandar a un no-SA a una
// ruta que para él no existe. Es el prefijo público "SA-", no la lista del backend: el reto
// del login (AuthContext) sigue saliendo a cualquier cuenta con un factor enrolado.
export const isMfaEnrollmentRole = (rol) => isSA(rol);

// ?mfa=requerido en la URL de enrolamiento = llegó redirigido por un 403 MFA_REQUIRED.
export const MFA_REQUIRED_PARAM = 'mfa';
export const MFA_REQUIRED_PARAM_VALUE = 'requerido';
// Código del cuerpo del 403 de auth.middleware.js.
export const MFA_REQUIRED_API_CODE = 'MFA_REQUIRED';
// Código propio (no es de Firebase) para "el usuario cerró el reto sin completar".
export const MFA_CANCELLED_CODE = 'mfa/cancelled';
// Emisor que muestra la app de autenticación junto al correo.
export const MFA_ISSUER = 'Goshofi';
export const TOTP_CODE_LENGTH = 6;

export const buildMfaRequiredUrl = () =>
  `${MFA_ENROLL_PATH}?${MFA_REQUIRED_PARAM}=${MFA_REQUIRED_PARAM_VALUE}`;

export const isMfaRequiredSearch = (search) => {
  try {
    return new URLSearchParams(search || '').get(MFA_REQUIRED_PARAM) === MFA_REQUIRED_PARAM_VALUE;
  } catch (e) {
    return false;
  }
};

// Respuesta 403 {code: "MFA_REQUIRED"} de un router V2 (error de axios).
export const isMfaRequiredApiError = (err) =>
  err?.response?.status === 403 && err?.response?.data?.code === MFA_REQUIRED_API_CODE;

// signInWithEmailAndPassword / signInWithPopup / reauthenticate* con un segundo factor enrolado.
export const isMultiFactorAuthRequired = (err) => err?.code === 'auth/multi-factor-auth-required';

export const mfaCancelledError = () =>
  Object.assign(new Error('El usuario cerró la verificación en dos pasos'), { code: MFA_CANCELLED_CODE });

export const isMfaCancelled = (err) => err?.code === MFA_CANCELLED_CODE;

// Proyecto sin Identity Platform o sin el proveedor TOTP habilitado: el servidor responde
// OPERATION_NOT_ALLOWED (o CONFIGURATION_NOT_FOUND) al iniciar el enrolamiento.
const TOTP_NOT_ENABLED_CODES = new Set(['auth/operation-not-allowed', 'auth/configuration-not-found']);
export const isTotpNotEnabledError = (err) => TOTP_NOT_ENABLED_CODES.has(err?.code);

export const isRecentLoginRequired = (err) => err?.code === 'auth/requires-recent-login';

export const isUnverifiedEmailError = (err) => err?.code === 'auth/unverified-email';

// Solo dígitos, máximo TOTP_CODE_LENGTH (acepta "123 456" pegado desde la app).
export const sanitizeTotpCode = (value) =>
  String(value ?? '').replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH);

export const isCompleteTotpCode = (value) => new RegExp(`^\\d{${TOTP_CODE_LENGTH}}$`).test(String(value ?? ''));

/**
 * Factores enrolados de un usuario de Firebase Auth: copia de multiFactor(user).enrolledFactors.
 * Lista vacía si no hay usuario o si el SDK falla. Recibe multiFactor por parámetro para que este
 * módulo siga sin depender del SDK. Lo usan la pantalla de doble factor y el menú de perfil.
 * @param {object|null|undefined} fbUser  auth.currentUser
 * @param {Function} multiFactorFn        multiFactor de firebase/auth
 * @returns {Array<object>}
 */
export const enrolledFactorsOf = (fbUser, multiFactorFn) => {
  if (!fbUser || typeof multiFactorFn !== 'function') return [];
  try {
    const factors = multiFactorFn(fbUser)?.enrolledFactors;
    return Array.isArray(factors) ? [...factors] : [];
  } catch (e) {
    return [];
  }
};

// Errores que no merecen mensaje: el usuario cerró la ventana de Google a propósito.
const SILENT_CODES = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request', MFA_CANCELLED_CODE]);

const ERROR_KEYS = {
  'auth/invalid-verification-code': 'mfa_error_invalid_code',
  'auth/missing-verification-code': 'mfa_code_invalid_format',
  'auth/invalid-multi-factor-session': 'mfa_error_session_expired',
  'auth/missing-multi-factor-session': 'mfa_error_session_expired',
  'auth/too-many-requests': 'mfa_error_too_many',
  'auth/network-request-failed': 'mfa_error_network',
  'auth/maximum-second-factor-count-exceeded': 'mfa_error_max_factors',
  'auth/second-factor-already-in-use': 'mfa_error_already_enrolled',
  'auth/unsupported-first-factor': 'mfa_error_unsupported_first_factor',
  'auth/unverified-email': 'mfa_unverified_email',
  'auth/operation-not-allowed': 'mfa_not_enabled',
  'auth/configuration-not-found': 'mfa_not_enabled',
  'auth/wrong-password': 'mfa_error_wrong_password',
  'auth/invalid-credential': 'mfa_error_wrong_password',
  'auth/invalid-login-credentials': 'mfa_error_wrong_password',
  'auth/user-mismatch': 'mfa_error_user_mismatch',
  'auth/requires-recent-login': 'mfa_reauth_body',
  'auth/popup-blocked': 'mfa_error_popup_blocked',
};

// Vencimientos: en el enrolamiento venció el secreto (QR); en el reto, la sesión del login.
const EXPIRED_CODES = new Set(['auth/code-expired', 'auth/invalid-verification-id']);

/**
 * Clave i18n para un error de Firebase Auth en el flujo de doble factor, o null si no
 * hay que mostrar nada (popup cerrado por el usuario, reto cancelado).
 * @param {unknown} err
 * @param {'challenge'|'enroll'|'reauth'|'remove'} context
 */
export const mfaErrorKey = (err, context = 'challenge') => {
  const code = err?.code;
  if (SILENT_CODES.has(code)) return null;
  if (EXPIRED_CODES.has(code)) return context === 'enroll' ? 'mfa_error_secret_expired' : 'mfa_error_session_expired';
  return ERROR_KEYS[code] || 'mfa_error_generic';
};

// ─── Quitar el doble factor de OTRO usuario (FEAT-20261026-mfa-totp, sesión 3) ───
// POST /usersV2/mfa-reset/:id. Quién puede hacerlo lo decide el backend (preset del router y
// req.user.secondFactor); GET /usersV2/detail/:id solo trae `mfa: {enrolled}` a ese rol.
// Códigos del cuerpo de error de functions/controllers/usersMfa.controller.js.
export const MFA_REQUIRED_FOR_ACTION_API_CODE = 'MFA_REQUIRED_FOR_ACTION';
export const MFA_NOT_ENROLLED_API_CODE = 'MFA_NOT_ENROLLED';

export const mfaResetPath = (uid) => `/usersV2/mfa-reset/${encodeURIComponent(String(uid ?? ''))}`;

// Factores enrolados según el detalle del usuario; 0 si no viene el campo o no es válido.
export const mfaEnrolledCount = (detail) => {
  const n = Number(detail?.mfa?.enrolled);
  return Number.isInteger(n) && n > 0 ? n : 0;
};

export const isMfaNotEnrolledApiError = (err) =>
  err?.response?.status === 409 && err?.response?.data?.code === MFA_NOT_ENROLLED_API_CODE;

/**
 * Clave i18n para un error de POST /usersV2/mfa-reset/:id (error de axios).
 * El 403 MFA_REQUIRED del middleware (rol con la exigencia activa) tiene la misma salida que
 * MFA_REQUIRED_FOR_ACTION: volver a entrar con el código.
 * @param {unknown} err
 */
export const mfaResetErrorKey = (err) => {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  if (status === 403 && (code === MFA_REQUIRED_FOR_ACTION_API_CODE || code === MFA_REQUIRED_API_CODE)) {
    return 'mfa_admin_reset_error_needs_mfa';
  }
  if (isMfaNotEnrolledApiError(err)) return 'mfa_admin_reset_error_not_enrolled';
  if (status === 404) return 'mfa_admin_reset_error_not_found';
  if (status === 403) return 'mfa_admin_reset_error_forbidden';
  return 'mfa_admin_reset_error_generic';
};

// Lleva al usuario a la pantalla de enrolamiento tras un 403 MFA_REQUIRED. Recarga completa
// a propósito: functionsClient vive fuera del router y la recarga limpia los toasts de error
// que hayan pintado las pantallas. Si ya está en la pantalla no hace nada (sin bucles: la
// propia pantalla y el layout reciben 403 hasta que el usuario se enrola).
export const redirectToMfaEnrollment = (win = typeof window !== 'undefined' ? window : undefined) => {
  if (!win || !win.location) return false;
  const here = String(win.location.pathname || '').replace(/\/+$/, '');
  if (here === MFA_ENROLL_PATH) return false;
  win.location.assign(buildMfaRequiredUrl());
  return true;
};

// ─── Puerta del doble factor para SA (FEAT-20261026-mfa-totp, sesión 5) ───
// Un SA al que el backend le exige doble factor y cuya sesión no lo trae no ve nada del portal:
// PrivateRouter muestra solo la pantalla de doble factor (o un cargador mientras se decide).
// AuthContext guarda el estado (mfaGate) y lo decide con resolveMfaGate al cargar el perfil.
export const MFA_GATE_CHECKING = 'checking';
export const MFA_GATE_OK = 'ok';
export const MFA_GATE_REQUIRED = 'required';

/**
 * Estado de la puerta al terminar de cargar el perfil. Quién EXIGE el factor lo decide solo el
 * backend (MFA_REQUIRED_ROLES): el cliente se entera por el 403 MFA_REQUIRED de my-permissions.
 * - rol que no es SA → ok (la puerta no aplica);
 * - el ID token ya trae el segundo factor (firebase.sign_in_second_factor) → ok;
 * - my-permissions respondió 403 MFA_REQUIRED → required;
 * - cualquier otro resultado (200, 500, red, timeout) → ok. El backend sigue exigiendo el factor
 *   en cada endpoint; el front no puede dejar a un SA sin portal por un fallo transitorio. Con
 *   MFA_REQUIRED_ROLES vacía (producción hoy) el 403 no existe, así que nadie queda bloqueado.
 * Nunca lanza.
 * @param {{rol?: unknown, tokenHasSecondFactor?: boolean, permissionsError?: unknown}} input
 * @returns {'ok'|'required'}
 */
export const resolveMfaGate = ({ rol, tokenHasSecondFactor = false, permissionsError = null } = {}) => {
  if (!isMfaEnrollmentRole(rol)) return MFA_GATE_OK;
  if (tokenHasSecondFactor === true) return MFA_GATE_OK;
  if (isMfaRequiredApiError(permissionsError)) return MFA_GATE_REQUIRED;
  return MFA_GATE_OK;
};

// Aviso de functionsClient a la puerta: un 403 MFA_REQUIRED que llega con el portal ya cargado
// (sesión vieja o variable recién cambiada). AuthContext se suscribe al montar y pasa la puerta a
// "required" sin recargar. Si nadie escucha, functionsClient conserva la recarga de
// redirectToMfaEnrollment como respaldo.
const mfaRequiredListeners = new Set();

/**
 * @param {() => void} listener
 * @returns {() => void} función para darse de baja
 */
export const subscribeMfaRequired = (listener) => {
  if (typeof listener !== 'function') return () => {};
  mfaRequiredListeners.add(listener);
  return () => {
    mfaRequiredListeners.delete(listener);
  };
};

/**
 * Avisa a los oyentes. true si al menos uno lo atendió sin lanzar; false si no hay ninguno (o
 * todos fallaron), y entonces el llamador recarga hacia la pantalla. Un oyente roto no tumba la
 * petición ni impide avisar a los demás.
 * @returns {boolean}
 */
export const notifyMfaRequired = () => {
  let handled = false;
  for (const listener of [...mfaRequiredListeners]) {
    try {
      listener();
      handled = true;
    } catch (e) {
      // Se ignora: si ningún oyente responde, functionsClient recarga.
    }
  }
  return handled;
};
